/** @jsxImportSource theme-ui @jsxRuntime classic */
import React, { useState, useRef, createContext, useContext } from 'react';

import type { Swiper as SwiperInstance } from 'swiper';

interface CarouselV2ContextProviderProps {
  /** Children to render within the context */
  children: React.ReactNode;
  /** Initial active slide index */
  initialIndex?: number;
  /** Optional reference to an existing Swiper instance */
  swiperInstance?: SwiperInstance | null;
}

interface CarouselContextType {
  /** Current active slide index */
  activeIndex: number;
  /** Whether the carousel is at the beginning */
  isBeginning: boolean;
  /** Whether the carousel is at the end */
  isEnd: boolean;
  /** Set the active slide index */
  setActiveIndex: (index: number) => void;
  /** Set the beginning state */
  setIsBeginning: (value: boolean) => void;
  /** Set the end state */
  setIsEnd: (value: boolean) => void;
  /** Navigate to the next slide */
  goNext: () => void;
  /** Navigate to the previous slide */
  goPrev: () => void;
  /** Navigate to a specific slide by index */
  goToSlide: (index: number) => void;
  /** Reference to the Swiper instance */
  swiperInstance: React.MutableRefObject<SwiperInstance | null>;
}

export const CarouselContextV2 = createContext<CarouselContextType | undefined>(
  undefined,
);

/**
 * Hook to access carousel context
 * Use this in nested components to control the carousel
 */
export const useCarouselContextV2 = (): CarouselContextType => {
  const context = useContext(CarouselContextV2);
  if (context === undefined) {
    throw new Error(
      'useCarouselContextV2 must be used within a CarouselV2ContextProvider',
    );
  }
  return context;
};

/**
 * Standalone context provider for CarouselV2 components
 *
 * This allows you to wrap multiple components in a shared carousel context,
 * enabling navigation components to be placed outside the carousel itself.
 */
export const CarouselV2ContextProvider = ({
  children,
  initialIndex = 0,
}: CarouselV2ContextProviderProps) => {
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  // Use external swiper instance if provided, otherwise use internal ref
  const swiperRef = useRef<SwiperInstance | null>(null);

  // Navigation methods
  const goNext = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext();
    }
  };

  const goPrev = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev();
    }
  };

  const goToSlide = (index: number) => {
    if (swiperRef.current) {
      swiperRef.current.slideTo(index);
    }
  };

  // Context value
  const contextValue = {
    activeIndex,
    isBeginning,
    isEnd,
    setActiveIndex,
    setIsBeginning,
    setIsEnd,
    goNext,
    goPrev,
    goToSlide,
    swiperInstance: swiperRef,
  };

  return (
    <CarouselContextV2.Provider value={contextValue}>
      {children}
    </CarouselContextV2.Provider>
  );
};

export default CarouselV2ContextProvider;
